import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection, query, orderBy, where,
  getDocs, limit, startAfter, Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { TRANSACTION_TYPES } from '../utils/stockUtils';
import printTable from '../utils/printUtils';
import * as XLSX from 'xlsx';
import { runGatekeeper, runQuickAnalysis, runDeepAnalysis } from '../utils/aiPipeline';

const PAGE_SIZE = 50;

const DATE_PRESETS = [
  { label: 'Last 24h',  value: 'day' },
  { label: 'Last 7d',   value: 'week' },
  { label: 'Last 30d',  value: 'month' },
  { label: 'Last Year', value: 'year' },
  { label: 'All time',  value: 'all' },
];

const SORT_OPTIONS = [
  { key: 'timestamp',    label: 'Time',       icon: '🕐' },
  { key: 'delta',        label: 'Change',     icon: '↕️' },
  { key: 'oldStock',     label: 'Old Stock',  icon: '📦' },
  { key: 'newStock',     label: 'New Stock',  icon: '📬' },
  { key: 'userName',     label: 'User',       icon: '👤' },
  { key: 'productGroup', label: 'Group',      icon: '🏷️' },
];

function getDateThreshold(preset) {
  const now = Date.now();
  switch (preset) {
    case 'day':   return new Date(now - 1   * 24 * 60 * 60 * 1000);
    case 'week':  return new Date(now - 7   * 24 * 60 * 60 * 1000);
    case 'month': return new Date(now - 30  * 24 * 60 * 60 * 1000);
    case 'year':  return new Date(now - 365 * 24 * 60 * 60 * 1000);
    default:      return null;
  }
}

function sortLogs(logs, sortKey, sortDir) {
  if (!sortKey || sortKey === 'timestamp') {
    return sortDir === 'asc' ? [...logs].reverse() : logs;
  }
  return [...logs].sort((a, b) => {
    let av = a[sortKey], bv = b[sortKey];
    if (av == null) av = '';
    if (bv == null) bv = '';
    if (typeof av === 'string') av = av.toLowerCase();
    if (typeof bv === 'string') bv = bv.toLowerCase();
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ?  1 : -1;
    return 0;
  });
}

// ── Agent progress stages ─────────────────────────────────────────────────────
const AGENT_STAGES = [
  { id: 'coordinator', label: 'Coordinator', icon: '🧠', desc: 'Plans execution' },
  { id: 'specialist',  label: 'Specialist',  icon: '⚙️', desc: 'Extracts filters' },
  { id: 'critic',      label: 'Critic',      icon: '🔍', desc: 'Reviews output' },
  { id: 'validator',   label: 'Validator',   icon: '✅', desc: 'Final check' },
];

function AgentProgress({ stages }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
      {AGENT_STAGES.map(agent => {
        const s = stages[agent.id] || { status: 'waiting' };
        const c = {
          waiting: { bg: '#f8fafc', border: '#e2e8f0', text: '#94a3b8', icon: agent.icon },
          running: { bg: '#fffbeb', border: '#fde68a', text: '#92400e', icon: '⏳' },
          done:    { bg: '#f0fdf4', border: '#bbf7d0', text: '#166534', icon: '✅' },
          error:   { bg: '#fef2f2', border: '#fecaca', text: '#991b1b', icon: '⚠️' },
          flagged: { bg: '#fff7ed', border: '#fed7aa', text: '#9a3412', icon: '🚩' },
        }[s.status] || { bg: '#f8fafc', border: '#e2e8f0', text: '#94a3b8', icon: agent.icon };
        return (
          <div key={agent.id} style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '7px 12px', borderRadius: '8px',
            backgroundColor: c.bg, border: `1px solid ${c.border}`,
            transition: 'all 0.25s ease',
          }}>
            <span style={{ fontSize: '13px', flexShrink: 0 }}>{c.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: '12px', fontWeight: '700', color: c.text }}>{agent.label}</span>
              {s.message && (
                <span style={{ fontSize: '11px', color: c.text, opacity: 0.75, marginLeft: '8px' }}>
                  — {s.message}
                </span>
              )}
            </div>
            {s.status === 'running' && (
              <span style={{
                width: '7px', height: '7px', borderRadius: '50%',
                backgroundColor: '#f59e0b', flexShrink: 0, display: 'inline-block',
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── NL Search Bar — multi-agent pipeline ──────────────────────────────────────
function NLSearchBar({ users, allGroups, onApply, onClear, isMobile }) {
  const [nlInput,        setNlInput]        = useState('');
  const [tier,           setTier]           = useState('quick');
  const [interpreting,   setInterpreting]   = useState(false);
  const [error,          setError]          = useState('');
  const [interpretation, setInterpretation] = useState('');
  const [resultTier,     setResultTier]     = useState(null);
  const [resultConf,     setResultConf]     = useState(null);
  const [resultWarning,  setResultWarning]  = useState(null);
  const [agentStages,    setAgentStages]    = useState({});
  const [apiCalls,       setApiCalls]       = useState(0);
  const [hasApplied,     setHasApplied]     = useState(false);
  const [showAgents,     setShowAgents]     = useState(false);
  const inputRef = useRef(null);

  const validTypes   = Object.keys(TRANSACTION_TYPES).join(', ');
  const validPresets = DATE_PRESETS.map(p => p.value).join(', ');
  const userList     = users.filter(u => u !== 'All').join(', ') || 'none known';
  const groupList    = allGroups.join(', ') || 'none known';

  const updateStage = (stage, status, message) =>
    setAgentStages(prev => ({ ...prev, [stage]: { status, message } }));

  const resetState = () => {
    setError(''); setInterpretation(''); setResultTier(null);
    setResultConf(null); setResultWarning(null);
    setAgentStages({}); setApiCalls(0);
  };

  const handleSubmit = async (chosenTier) => {
    const q = nlInput.trim();
    if (!q) return;
    setInterpreting(true);
    resetState();
    if (chosenTier === 'deep') {
      setShowAgents(true);
      AGENT_STAGES.forEach(a => updateStage(a.id, 'waiting', ''));
    } else {
      setShowAgents(false);
    }

    try {
      // ── Gatekeeper ─────────────────────────────────────────────────────────
      const gate = await runGatekeeper(q);
      const gateCalls = gate.fromCache ? 0 : 1;

      if (!gate.valid) {
        setError(`Not an inventory query — try "sales last week" or "write-offs by Rahul".`);
        setInterpreting(false);
        return;
      }
      if (gate.clarifyingQuestion) {
        setError(`Clarification needed: ${gate.clarifyingQuestion}`);
        setInterpreting(false);
        return;
      }

      let result;

      if (chosenTier === 'quick') {
        result = await runQuickAnalysis(q, validTypes, validPresets, userList, groupList);
        setApiCalls(gateCalls + (result.fromCache ? 0 : 1));
        if (result.confidence === 'low' || result.suggestDeepAnalysis) {
          setError('Quick Analysis is uncertain — try Deep Analysis (🔍) for a more thorough result.');
          setResultConf('low');
          setInterpreting(false);
          return;
        }
      } else {
        result = await runDeepAnalysis(
          q, validTypes, validPresets, userList, groupList,
          ({ stage, status, message }) => updateStage(stage, status, message)
        );
        setApiCalls(gateCalls + (result.apiCallsUsed || 0));
      }

      // Apply all filter fields returned by AI
      onApply({
        search:       result.search       ?? '',
        filterTypes:  result.filterTypes  ?? [],
        filterUser:   result.filterUser   ?? 'All',
        filterGroups: result.filterGroups ?? [],
        datePreset:   result.datePreset   ?? 'month',
        sortKey:      result.sortKey      ?? 'timestamp',
        sortDir:      result.sortDir      ?? 'desc',
      });

      setInterpretation(result.interpretation || '');
      setResultTier(result.tier);
      setResultConf(result.confidence);
      setResultWarning(result.warning || null);
      setHasApplied(true);

    } catch (err) {
      const is503 = err.message?.includes('503');
      setError(is503
        ? 'Gemini is temporarily busy — wait a few seconds and try again.'
        : `Could not interpret query — ${err.message}`
      );
      console.error('AI pipeline error:', err);
    } finally {
      setInterpreting(false);
    }
  };

  const handleClear = () => {
    setNlInput(''); resetState();
    setHasApplied(false); setShowAgents(false);
    onClear();
    inputRef.current?.focus();
  };

  const confColor = { high: '#16a34a', medium: '#ca8a04', low: '#ef4444' }[resultConf] || '#94a3b8';
  const tierLabel = resultTier === 'deep' ? '🔍 Deep' : resultTier === 'quick' ? '⚡ Quick' : null;
  const doneAgents = Object.values(agentStages).filter(s => s.status === 'done').length;

  return (
    <div style={nlStyles.wrapper}>
      {/* Tier selector */}
      <div style={nlStyles.tierRow}>
        <span style={nlStyles.tierLabel}>AI Analysis:</span>
        <button
          onClick={() => setTier('quick')}
          style={{ ...nlStyles.tierBtn, ...(tier === 'quick' ? nlStyles.tierBtnActiveQuick : {}) }}
          title="2 API calls · ~1.5s · Good for simple queries"
        >⚡ Quick</button>
        <button
          onClick={() => setTier('deep')}
          style={{ ...nlStyles.tierBtn, ...(tier === 'deep' ? nlStyles.tierBtnActiveDeep : {}) }}
          title="3–5 API calls · ~6–8s · Gatekeeper → Coordinator → Specialist → Critic → Validator"
        >🔍 Deep</button>
        {apiCalls > 0 && (
          <span style={nlStyles.apiCallBadge}>{apiCalls} API call{apiCalls !== 1 ? 's' : ''}</span>
        )}
      </div>

      {/* Input */}
      <div style={nlStyles.inputRow}>
        <span style={nlStyles.aiIcon}>✨</span>
        <input
          ref={inputRef}
          type="text"
          value={nlInput}
          onChange={e => {
            setNlInput(e.target.value);
            if (hasApplied) { setInterpretation(''); setHasApplied(false); setShowAgents(false); }
          }}
          onKeyDown={e => { if (e.key === 'Enter') handleSubmit(tier); if (e.key === 'Escape') handleClear(); }}
          placeholder={tier === 'deep'
            ? 'Ask anything — e.g. "which products in stationary had write-offs?"'
            : 'Ask in plain English — e.g. "write-offs by Rahul last week" or "sales in stationary"'}
          style={{ ...nlStyles.input, fontSize: isMobile ? '16px' : '14px' }}
          disabled={interpreting}
        />
        {nlInput && !interpreting && (
          <button onClick={handleClear} style={nlStyles.clearBtn}>✕</button>
        )}
        <button
          onClick={() => handleSubmit(tier)}
          disabled={interpreting || !nlInput.trim()}
          style={interpreting || !nlInput.trim() ? nlStyles.searchBtnDisabled : nlStyles.searchBtn}
        >
          {interpreting ? '…' : 'Search'}
        </button>
      </div>

      {/* Deep agent progress */}
      {tier === 'deep' && showAgents && (interpreting || hasApplied) && (
        <AgentProgress stages={agentStages} />
      )}

      {/* Interpretation tag */}
      {interpretation && !error && (
        <div style={nlStyles.interpretationTag}>
          <span style={nlStyles.aiLabel}>AI interpreted:</span>
          <span style={nlStyles.interpretationText}>{interpretation}</span>
          {tierLabel && (
            <span style={{
              ...nlStyles.tierBadge,
              color: resultTier === 'deep' ? '#7c3aed' : '#1d4ed8',
              borderColor: resultTier === 'deep' ? '#c4b5fd' : '#bfdbfe',
              backgroundColor: resultTier === 'deep' ? '#f5f3ff' : '#eff6ff',
            }}>{tierLabel}</span>
          )}
          {resultConf && (
            <span style={{ ...nlStyles.confBadge, color: confColor, borderColor: confColor + '44' }}>
              {resultConf} confidence
            </span>
          )}
          <button onClick={handleClear} style={nlStyles.clearFilterBtn}>Clear AI filters</button>
        </div>
      )}

      {/* Validator warning */}
      {resultWarning && (
        <div style={nlStyles.warningTag}>
          🚩 <strong>Validator flagged:</strong> {resultWarning}
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={nlStyles.errorTag}>
          <span>⚠️ {error}</span>
          <button onClick={() => setError('')} style={nlStyles.dismissBtn}>✕</button>
        </div>
      )}

      {/* Quick loading */}
      {interpreting && tier === 'quick' && (
        <div style={nlStyles.loadingTag}>
          <span style={nlStyles.loadingDot} />
          Running Quick Analysis — Gatekeeper → Executor…
        </div>
      )}

      {/* Deep completed summary */}
      {!interpreting && showAgents && hasApplied && (
        <div style={nlStyles.deepSummary}>
          <span style={nlStyles.deepSummaryText}>
            🔍 Deep complete · {apiCalls} API calls · {doneAgents}/{AGENT_STAGES.length} agents passed
          </span>
          <button onClick={() => setShowAgents(false)} style={nlStyles.collapseBtn}>Hide details</button>
        </div>
      )}
    </div>
  );
}

const nlStyles = {
  wrapper:          { backgroundColor: '#fafbff', border: '1.5px solid #c7d2fe', borderRadius: '12px', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '8px' },
  tierRow:          { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  tierLabel:        { fontSize: '12px', fontWeight: '700', color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.06em' },
  tierBtn:          { padding: '5px 14px', borderRadius: '20px', border: '1.5px solid #e2e8f0', backgroundColor: '#ffffff', color: '#64748b', fontSize: '12px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.15s' },
  tierBtnActiveQuick: { backgroundColor: '#eff6ff', borderColor: '#3b82f6', color: '#1d4ed8' },
  tierBtnActiveDeep:  { backgroundColor: '#f5f3ff', borderColor: '#7c3aed', color: '#6d28d9' },
  apiCallBadge:     { fontSize: '11px', color: '#94a3b8', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '2px 8px', borderRadius: '20px', marginLeft: 'auto' },
  inputRow:         { display: 'flex', alignItems: 'center', gap: '8px' },
  aiIcon:           { fontSize: '16px', flexShrink: 0 },
  input:            { flex: 1, padding: '9px 12px', border: '1.5px solid #e0e7ff', borderRadius: '8px', backgroundColor: '#ffffff', color: '#1e293b', outline: 'none', fontFamily: 'inherit', fontWeight: '500' },
  clearBtn:         { background: 'none', border: 'none', color: '#94a3b8', fontSize: '14px', cursor: 'pointer', padding: '4px 6px', flexShrink: 0 },
  searchBtn:        { padding: '9px 18px', backgroundColor: '#6366f1', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', flexShrink: 0, boxShadow: '0 4px 12px rgba(99,102,241,0.3)' },
  searchBtnDisabled:{ padding: '9px 18px', backgroundColor: '#c7d2fe', color: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'not-allowed', flexShrink: 0 },
  interpretationTag:{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '8px', padding: '8px 12px', fontSize: '12px' },
  aiLabel:          { fontWeight: '800', color: '#4f46e5', flexShrink: 0, fontSize: '12px' },
  interpretationText:{ color: '#374151', flex: 1, fontWeight: '500', minWidth: '120px' },
  tierBadge:        { fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px', border: '1px solid', flexShrink: 0 },
  confBadge:        { fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '20px', backgroundColor: '#ffffff', border: '1px solid', flexShrink: 0 },
  clearFilterBtn:   { padding: '4px 12px', backgroundColor: '#ffffff', border: '1.5px solid #c7d2fe', color: '#6366f1', borderRadius: '8px', fontSize: '11px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 },
  warningTag:       { backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: '#9a3412', lineHeight: 1.5 },
  errorTag:         { display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', color: '#dc2626', fontWeight: '500' },
  dismissBtn:       { background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '13px', padding: '0 4px' },
  loadingTag:       { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#6366f1', fontStyle: 'italic', fontWeight: '500' },
  loadingDot:       { width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#6366f1', display: 'inline-block', flexShrink: 0 },
  deepSummary:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#f5f3ff', border: '1px solid #c4b5fd', borderRadius: '8px', padding: '8px 12px' },
  deepSummaryText:  { fontSize: '12px', color: '#6d28d9', fontWeight: '600' },
  collapseBtn:      { fontSize: '11px', color: '#7c3aed', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontWeight: '600', padding: '0 4px' },
};

// ── Main component ─────────────────────────────────────────────────────────────
export default function ActivityLog() {
  const { orgId } = useAuth();

  const [logs,        setLogs]        = useState([]);
  const [lastDoc,     setLastDoc]     = useState(null);
  const [hasMore,     setHasMore]     = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isMobile,    setIsMobile]    = useState(() => window.innerWidth < 640);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const [search,       setSearch]       = useState('');
  const [filterTypes,  setFilterTypes]  = useState([]);
  const [filterUser,   setFilterUser]   = useState('All');
  const [filterGroups, setFilterGroups] = useState([]);   // ← AI group filter
  const [datePreset,   setDatePreset]   = useState('month');
  const [sortKey,      setSortKey]      = useState('timestamp');
  const [sortDir,      setSortDir]      = useState('desc');

  const handleNLApply = useCallback((result) => {
    if (result.search       !== undefined) setSearch(result.search);
    if (result.filterTypes  !== undefined) setFilterTypes(result.filterTypes);
    if (result.filterUser   !== undefined) setFilterUser(result.filterUser);
    if (result.filterGroups !== undefined) setFilterGroups(result.filterGroups);
    if (result.datePreset   !== undefined) setDatePreset(result.datePreset);
    if (result.sortKey      !== undefined) setSortKey(result.sortKey);
    if (result.sortDir      !== undefined) setSortDir(result.sortDir);
  }, []);

  const handleNLClear = useCallback(() => {
    setSearch(''); setFilterTypes([]); setFilterUser('All');
    setFilterGroups([]); setDatePreset('month');
    setSortKey('timestamp'); setSortDir('desc');
  }, []);

  const buildQuery = useCallback((cursor = null) => {
    const threshold = getDateThreshold(datePreset);
    const constraints = [
      where('orgId', '==', orgId),
      orderBy('timestamp', 'desc'),
    ];
    if (threshold) constraints.push(where('timestamp', '>=', Timestamp.fromDate(threshold)));
    constraints.push(limit(PAGE_SIZE));
    if (cursor) constraints.push(startAfter(cursor));
    return query(collection(db, 'activityLog'), ...constraints);
  }, [orgId, datePreset]);

  const fetchFirstPage = useCallback(async () => {
    if (!orgId) return;
    setLoading(true); setLogs([]); setLastDoc(null); setHasMore(false);
    try {
      const snap = await getDocs(buildQuery());
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setLogs(data);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(false);
    }
  }, [buildQuery, orgId]);

  const fetchNextPage = async () => {
    if (!lastDoc || loadingMore) return;
    setLoadingMore(true);
    try {
      const snap = await getDocs(buildQuery(lastDoc));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setLogs(prev => [...prev, ...data]);
      setLastDoc(snap.docs[snap.docs.length - 1] || null);
      setHasMore(snap.docs.length === PAGE_SIZE);
    } catch (err) {
      console.error('Failed to fetch more logs:', err);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { fetchFirstPage(); }, [fetchFirstPage]);

  const toggleType = (type) =>
    setFilterTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);

  const handleSortClick = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const users     = ['All', ...new Set(logs.map(l => l.userName).filter(Boolean))];
  const allGroups = [...new Set(logs.map(l => l.productGroup).filter(Boolean))];

  const filtered = sortLogs(
    logs
      .filter(l => filterTypes.length === 0  || filterTypes.includes(l.transactionType))
      .filter(l => filterUser === 'All'       || l.userName === filterUser)
      .filter(l => filterGroups.length === 0  || filterGroups.some(g =>
        (l.productGroup || '').toLowerCase() === g.toLowerCase()
      ))
      .filter(l => !search || l.productName?.toLowerCase().includes(search.toLowerCase())),
    sortKey, sortDir
  );

  const exportToExcel = () => {
    const rows = filtered.map(l => ({
      'Time':      l.timestamp?.toDate().toLocaleString() || '',
      'Product':   l.productName,
      'Group':     l.productGroup,
      'Type':      TRANSACTION_TYPES[l.transactionType]?.label || l.transactionType,
      'Delta':     l.delta > 0 ? `+${l.delta}` : l.delta,
      'Old Stock': l.oldStock,
      'New Stock': l.newStock,
      'User':      l.userName,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Activity Log');
    XLSX.writeFile(wb, 'activity-log.xlsx');
  };

  const printLog = () => {
    printTable({
      title: 'Activity Log',
      subtitle: `Period: ${DATE_PRESETS.find(p => p.value === datePreset)?.label} · ${filtered.length} entries`,
      columns: ['Time', 'Product', 'Group', 'Type', 'Change', 'Old Stock', 'New Stock', 'User'],
      rows: filtered.map(l => {
        const txn = TRANSACTION_TYPES[l.transactionType];
        const delta = l.delta;
        return [
          l.timestamp?.toDate().toLocaleString() || '—',
          l.productName, l.productGroup || '—',
          txn?.label || l.transactionType,
          { value: delta > 0 ? `+${delta}` : delta, style: `color:${delta > 0 ? '#16a34a' : delta < 0 ? '#ef4444' : '#64748b'};font-weight:700;` },
          { value: l.oldStock, style: 'color:#94a3b8;' },
          { value: l.newStock, style: 'font-weight:600;' },
          l.userName || '—',
        ];
      }),
      styles: { colWidths: ['18%', '16%', '10%', '12%', '8%', '8%', '8%', '12%'] },
    });
  };

  const ActionButtons = ({ mobile }) => (
    <div style={{ display: 'flex', gap: '8px' }}>
      <button onClick={exportToExcel} style={mobile ? mobileStyles.exportBtn : styles.exportBtn}>
        ⬇ {mobile ? 'Export' : 'Export Excel'}
      </button>
      <button onClick={printLog} style={mobile ? mobileStyles.printBtn : styles.printBtn}>
        🖨️ Print
      </button>
    </div>
  );

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <span style={{ color: '#cbd5e1', marginLeft: '4px' }}>⇅</span>;
    return <span style={{ color: '#3b82f6', marginLeft: '4px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  // Active group filter pill row (shown when AI set group filters)
  const GroupFilterPills = () => {
    if (filterGroups.length === 0) return null;
    return (
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', color: '#6366f1', fontWeight: '600' }}>Groups:</span>
        {filterGroups.map(g => (
          <span key={g} style={{
            padding: '3px 10px', borderRadius: '20px', fontSize: '12px',
            backgroundColor: '#eef2ff', color: '#6366f1', border: '1px solid #c7d2fe',
            fontWeight: '600', display: 'flex', alignItems: 'center', gap: '4px',
          }}>
            {g}
            <button
              onClick={() => setFilterGroups(prev => prev.filter(x => x !== g))}
              style={{ background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer', fontSize: '11px', padding: '0 2px', lineHeight: 1 }}
            >✕</button>
          </span>
        ))}
        <button
          onClick={() => setFilterGroups([])}
          style={{ fontSize: '11px', color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
        >clear</button>
      </div>
    );
  };

  // ── Mobile log card ──────────────────────────────────────────────────────────
  const MobileLogCard = ({ log }) => {
    const txn   = TRANSACTION_TYPES[log.transactionType];
    const delta = log.delta;
    return (
      <div style={mobileStyles.card}>
        <div style={mobileStyles.cardTopRow}>
          <div style={{ flex: 1, marginRight: '8px' }}>
            <span style={mobileStyles.productName}>{log.productName}</span>
            {log.productGroup && <span style={mobileStyles.groupBadge}>{log.productGroup}</span>}
          </div>
          <span style={{ ...mobileStyles.typeBadge, backgroundColor: txn?.bg || '#f1f5f9', color: txn?.color || '#64748b', border: `1px solid ${(txn?.color || '#e2e8f0')}44` }}>
            {txn?.label || log.transactionType}
          </span>
        </div>
        <div style={mobileStyles.metaRow}>
          <span style={{ ...mobileStyles.delta, color: delta > 0 ? '#16a34a' : delta < 0 ? '#ef4444' : '#64748b' }}>
            {delta > 0 ? `+${delta}` : delta}
          </span>
          <span style={mobileStyles.stockFlow}>{log.oldStock} → {log.newStock}</span>
          <span style={mobileStyles.metaText}>{log.userName}</span>
        </div>
        <div style={mobileStyles.timestamp}>{log.timestamp?.toDate().toLocaleString() || '—'}</div>
      </div>
    );
  };

  const MobileSortBar = () => (
    <div style={mobileStyles.sortSection}>
      <p style={mobileStyles.filterSectionLabel}>Sort by</p>
      <div style={mobileStyles.sortScrollRow}>
        {SORT_OPTIONS.map(opt => {
          const isActive = sortKey === opt.key;
          return (
            <button key={opt.key} onClick={() => handleSortClick(opt.key)} style={{
              ...mobileStyles.sortPill,
              ...(isActive ? mobileStyles.sortPillActive : {}),
            }}>
              {opt.icon} {opt.label}
              {isActive && <span style={{ marginLeft: '4px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );

  const MobileFilters = () => (
    <div style={mobileStyles.filtersWrapper}>
      {/* AI Search */}
      <div>
        <p style={mobileStyles.filterSectionLabel}>✨ AI Search</p>
        <NLSearchBar users={users} allGroups={allGroups} onApply={handleNLApply} onClear={handleNLClear} isMobile={true} />
      </div>

      <input type="text" placeholder="Search products…" value={search}
        onChange={e => setSearch(e.target.value)} style={mobileStyles.search} />

      {/* Active group pills from AI */}
      {filterGroups.length > 0 && (
        <div>
          <p style={mobileStyles.filterSectionLabel}>Filtered Groups</p>
          <GroupFilterPills />
        </div>
      )}

      <div>
        <p style={mobileStyles.filterSectionLabel}>Period</p>
        <div style={mobileStyles.scrollRow}>
          {DATE_PRESETS.map(p => (
            <button key={p.value} onClick={() => setDatePreset(p.value)}
              style={datePreset === p.value ? mobileStyles.pillActive : mobileStyles.pillInactive}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p style={mobileStyles.filterSectionLabel}>Type</p>
        <div style={mobileStyles.scrollRow}>
          <button onClick={() => setFilterTypes([])}
            style={filterTypes.length === 0 ? mobileStyles.pillActive : mobileStyles.pillInactive}>All</button>
          {Object.entries(TRANSACTION_TYPES).map(([type, txn]) => {
            const isActive = filterTypes.includes(type);
            return (
              <button key={type} onClick={() => toggleType(type)} style={{
                ...mobileStyles.pillInactive,
                backgroundColor: isActive ? txn.color : '#ffffff',
                color: isActive ? '#ffffff' : '#64748b',
                borderColor: isActive ? txn.color : '#e2e8f0',
                fontWeight: isActive ? '600' : '400',
              }}>{txn.label}</button>
            );
          })}
        </div>
      </div>

      <MobileSortBar />

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <p style={{ ...mobileStyles.filterSectionLabel, marginBottom: 0, whiteSpace: 'nowrap' }}>User</p>
        <select value={filterUser} onChange={e => setFilterUser(e.target.value)} style={mobileStyles.select}>
          {users.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
      </div>

      {(filterTypes.length > 0 || filterUser !== 'All' || search || filterGroups.length > 0) && (
        <p style={mobileStyles.filterHint}>
          Filtering {filtered.length} of {logs.length} entries.
          {hasMore && ' Load more to search further back.'}
        </p>
      )}
    </div>
  );

  // ── MOBILE LAYOUT ────────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ padding: '0 0 24px' }}>
        <div style={mobileStyles.header}>
          <div>
            <h1 style={mobileStyles.title}>Activity Log</h1>
            <p style={mobileStyles.subtitle}>
              {loading ? 'Loading…' : `${filtered.length} of ${logs.length} entries`}
            </p>
          </div>
          <ActionButtons mobile={true} />
        </div>
        <MobileFilters />
        <div style={{ padding: '0 16px' }}>
          {loading ? (
            <div style={mobileStyles.emptyState}>Loading activity log…</div>
          ) : filtered.length === 0 ? (
            <div style={mobileStyles.emptyState}>
              {logs.length === 0 ? 'No activity in this period' : 'No entries match your filters'}
            </div>
          ) : (
            filtered.map(log => <MobileLogCard key={log.id} log={log} />)
          )}
        </div>
        {hasMore && !loading && (
          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
            <button onClick={fetchNextPage} disabled={loadingMore}
              style={loadingMore ? mobileStyles.loadMoreDisabled : mobileStyles.loadMore}>
              {loadingMore ? 'Loading…' : `Load ${PAGE_SIZE} more`}
            </button>
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>{logs.length} loaded so far</span>
          </div>
        )}
        {!hasMore && logs.length > 0 && !loading && (
          <p style={{ textAlign: 'center', fontSize: '12px', color: '#cbd5e1', padding: '12px 0' }}>
            All {logs.length} entries loaded
          </p>
        )}
      </div>
    );
  }

  // ── DESKTOP LAYOUT ───────────────────────────────────────────────────────────
  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Activity Log</h1>
          <p style={styles.subtitle}>
            {loading ? 'Loading...' : `Showing ${filtered.length} of ${logs.length} loaded entries`}
          </p>
        </div>
        <ActionButtons mobile={false} />
      </div>

      <div style={styles.filtersWrapper}>
        {/* AI Search */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
            <span style={styles.filterLabel}>✨ AI Search</span>
            <span style={styles.aiHint}>powered by Gemini · pre-fills filters below</span>
          </div>
          <NLSearchBar users={users} allGroups={allGroups} onApply={handleNLApply} onClear={handleNLClear} isMobile={false} />
        </div>

        <div style={styles.filterDivider} />

        {/* Group pills (when AI filters by group) */}
        {filterGroups.length > 0 && (
          <div style={{ ...styles.filterRow, alignItems: 'center' }}>
            <span style={styles.filterLabel}>Groups:</span>
            <GroupFilterPills />
          </div>
        )}

        <input type="text" placeholder="Search products... (within loaded entries)"
          value={search} onChange={e => setSearch(e.target.value)} style={styles.search} />

        <div style={styles.filterRow}>
          <span style={styles.filterLabel}>Period:</span>
          <div style={styles.pillGroup}>
            {DATE_PRESETS.map(p => (
              <button key={p.value} onClick={() => setDatePreset(p.value)}
                style={datePreset === p.value ? styles.pillActive : styles.pillInactive}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div style={styles.filterRow}>
          <span style={styles.filterLabel}>Type:</span>
          <div style={styles.pillGroup}>
            <button onClick={() => setFilterTypes([])}
              style={filterTypes.length === 0 ? styles.pillActive : styles.pillInactive}>All</button>
            {Object.entries(TRANSACTION_TYPES).map(([type, txn]) => {
              const isActive = filterTypes.includes(type);
              return (
                <button key={type} onClick={() => toggleType(type)} style={{
                  ...styles.pillInactive,
                  backgroundColor: isActive ? txn.color : '#ffffff',
                  color: isActive ? '#ffffff' : '#64748b',
                  borderColor: isActive ? txn.color : '#e2e8f0',
                  fontWeight: isActive ? '600' : '400',
                }}>{txn.label}</button>
              );
            })}
          </div>
        </div>

        <div style={styles.filterRow}>
          <span style={styles.filterLabel}>User:</span>
          <select value={filterUser} onChange={e => setFilterUser(e.target.value)} style={styles.select}>
            {users.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>

        {/* Sort bar */}
        <div style={styles.filterRow}>
          <span style={styles.filterLabel}>Sort:</span>
          <div style={styles.pillGroup}>
            {SORT_OPTIONS.map(opt => {
              const isActive = sortKey === opt.key;
              return (
                <button key={opt.key} onClick={() => handleSortClick(opt.key)} style={{
                  ...styles.pillInactive,
                  ...(isActive ? { backgroundColor: '#eef2ff', borderColor: '#6366f1', color: '#4f46e5', fontWeight: '700' } : {}),
                }}>
                  {opt.icon} {opt.label}
                  {isActive && <span style={{ marginLeft: '3px' }}>{sortDir === 'asc' ? '↑' : '↓'}</span>}
                </button>
              );
            })}
          </div>
        </div>

        {(filterTypes.length > 0 || filterUser !== 'All' || search || filterGroups.length > 0) && (
          <p style={styles.filterHint}>
            Filtering within {logs.length} loaded entries.
            {hasMore && ' Load more entries below to search further back.'}
          </p>
        )}
      </div>

      <div style={styles.tableWrapper}>
        {loading ? (
          <div style={styles.loadingState}><p style={styles.loadingText}>Loading activity log...</p></div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                {[
                  { label: 'Time',      key: 'timestamp' },
                  { label: 'Product',   key: null },
                  { label: 'Group',     key: 'productGroup' },
                  { label: 'Type',      key: null },
                  { label: 'Change',    key: 'delta' },
                  { label: 'Old Stock', key: 'oldStock' },
                  { label: 'New Stock', key: 'newStock' },
                  { label: 'User',      key: 'userName' },
                ].map(({ label, key }) => (
                  <th key={label}
                    style={{
                      ...styles.th,
                      cursor: key ? 'pointer' : 'default', userSelect: 'none',
                      backgroundColor: sortKey === key ? '#eef2ff' : '#f8fafc',
                      color: sortKey === key ? '#4f46e5' : '#64748b',
                    }}
                    onClick={() => key && handleSortClick(key)}
                  >
                    {label}{key && <SortIcon col={key} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(log => {
                const txn   = TRANSACTION_TYPES[log.transactionType];
                const delta = log.delta;
                return (
                  <tr key={log.id} style={styles.tr}>
                    <td style={{ ...styles.td, color: '#94a3b8', whiteSpace: 'nowrap', fontSize: '12px' }}>
                      {log.timestamp?.toDate().toLocaleString() || '—'}
                    </td>
                    <td style={styles.tdName}>{log.productName}</td>
                    <td style={styles.td}><span style={styles.groupBadge}>{log.productGroup}</span></td>
                    <td style={styles.td}>
                      <span style={{ backgroundColor: txn?.bg || '#f1f5f9', color: txn?.color || '#64748b', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', border: `1px solid ${txn?.color || '#e2e8f0'}44` }}>
                        {txn?.label || log.transactionType}
                      </span>
                    </td>
                    <td style={{ ...styles.td, fontWeight: '700', fontSize: '15px', color: delta > 0 ? '#16a34a' : delta < 0 ? '#ef4444' : '#64748b' }}>
                      {delta > 0 ? `+${delta}` : delta}
                    </td>
                    <td style={{ ...styles.td, color: '#94a3b8' }}>{log.oldStock}</td>
                    <td style={{ ...styles.td, fontWeight: '600', color: '#1e293b' }}>{log.newStock}</td>
                    <td style={{ ...styles.td, color: '#64748b', fontSize: '13px' }}>{log.userName}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && !loading && (
                <tr>
                  <td colSpan="8" style={styles.emptyRow}>
                    {logs.length === 0 ? 'No activity in this period' : 'No entries match your filters'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {hasMore && !loading && (
        <div style={styles.loadMoreWrapper}>
          <button onClick={fetchNextPage} disabled={loadingMore}
            style={loadingMore ? styles.loadMoreBtnDisabled : styles.loadMoreBtn}>
            {loadingMore ? 'Loading...' : `Load ${PAGE_SIZE} more entries`}
          </button>
          <p style={styles.loadMoreHint}>{logs.length} entries loaded so far</p>
        </div>
      )}
      {!hasMore && logs.length > 0 && !loading && (
        <p style={styles.allLoadedHint}>All {logs.length} entries loaded for this period</p>
      )}
    </div>
  );
}

const mobileStyles = {
  header:       { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 16px 12px' },
  title:        { fontSize: '20px', fontWeight: '700', color: '#1e293b', marginBottom: '2px' },
  subtitle:     { fontSize: '13px', color: '#94a3b8' },
  exportBtn:    { padding: '8px 14px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#475569', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', flexShrink: 0 },
  printBtn:     { padding: '8px 14px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#475569', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  filtersWrapper:{ margin: '0 16px 12px', backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px' },
  search:       { width: '100%', boxSizing: 'border-box', padding: '10px 14px', backgroundColor: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', fontSize: '16px' },
  filterSectionLabel: { fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '7px' },
  scrollRow:    { display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '2px', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' },
  sortSection:  {},
  sortScrollRow:{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '2px', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none' },
  sortPill:     { padding: '6px 12px', borderRadius: '20px', border: '1.5px solid #e2e8f0', backgroundColor: '#ffffff', color: '#64748b', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, fontWeight: '500' },
  sortPillActive:{ backgroundColor: '#eef2ff', borderColor: '#6366f1', color: '#4f46e5', fontWeight: '700' },
  pillActive:   { padding: '5px 13px', borderRadius: '20px', border: '1.5px solid #3b82f6', backgroundColor: '#eff6ff', color: '#3b82f6', fontSize: '12px', cursor: 'pointer', fontWeight: '700', whiteSpace: 'nowrap', flexShrink: 0 },
  pillInactive: { padding: '5px 13px', borderRadius: '20px', border: '1.5px solid #e2e8f0', backgroundColor: '#ffffff', color: '#64748b', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  select:       { flex: 1, padding: '7px 12px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', fontSize: '14px', cursor: 'pointer' },
  filterHint:   { fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' },
  card:         { backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '13px', marginBottom: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  cardTopRow:   { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' },
  productName:  { fontSize: '14px', fontWeight: '600', color: '#1e293b', display: 'block', marginBottom: '4px' },
  groupBadge:   { backgroundColor: '#eff6ff', color: '#3b82f6', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '500' },
  typeBadge:    { padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', flexShrink: 0 },
  metaRow:      { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' },
  delta:        { fontSize: '16px', fontWeight: '800' },
  stockFlow:    { fontSize: '12px', color: '#64748b', backgroundColor: '#f8fafc', padding: '2px 8px', borderRadius: '6px' },
  metaText:     { fontSize: '12px', color: '#64748b' },
  timestamp:    { fontSize: '11px', color: '#94a3b8' },
  emptyState:   { padding: '60px 20px', textAlign: 'center', color: '#94a3b8', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0' },
  loadMore:     { width: '100%', padding: '12px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#475569', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  loadMoreDisabled: { width: '100%', padding: '12px', backgroundColor: '#f8fafc', border: '1.5px solid #e2e8f0', color: '#94a3b8', borderRadius: '10px', fontSize: '14px', cursor: 'not-allowed' },
};

const styles = {
  header:       { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  title:        { fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' },
  subtitle:     { fontSize: '14px', color: '#64748b' },
  exportBtn:    { padding: '9px 18px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#475569', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  printBtn:     { padding: '9px 18px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#475569', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  filtersWrapper:{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px', backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px 16px' },
  aiHint:       { fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' },
  filterDivider:{ borderTop: '1px solid #f1f5f9', margin: '2px 0' },
  search:       { padding: '9px 14px', backgroundColor: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', fontSize: '14px', width: '300px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  filterRow:    { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  filterLabel:  { fontSize: '12px', fontWeight: '600', color: '#64748b', whiteSpace: 'nowrap', minWidth: '44px' },
  pillGroup:    { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  pillActive:   { padding: '5px 14px', borderRadius: '20px', border: 'none', backgroundColor: '#3b82f6', color: '#fff', fontSize: '12px', cursor: 'pointer', fontWeight: '600' },
  pillInactive: { padding: '5px 14px', borderRadius: '20px', border: '1.5px solid #e2e8f0', backgroundColor: '#ffffff', color: '#64748b', fontSize: '12px', cursor: 'pointer' },
  select:       { padding: '7px 12px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: '8px', color: '#1e293b', fontSize: '13px', cursor: 'pointer' },
  filterHint:   { fontSize: '12px', color: '#94a3b8', fontStyle: 'italic', marginTop: '2px' },
  tableWrapper: { backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'auto', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  loadingState: { padding: '60px', textAlign: 'center' },
  loadingText:  { color: '#94a3b8', fontSize: '14px' },
  table:        { width: '100%', borderCollapse: 'collapse', minWidth: '700px' },
  th:           { padding: '13px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0', transition: 'background-color 0.15s' },
  tr:           { borderBottom: '1px solid #f1f5f9' },
  tdName:       { padding: '13px 16px', color: '#1e293b', fontSize: '14px', fontWeight: '500' },
  td:           { padding: '13px 16px', color: '#374151', fontSize: '14px' },
  groupBadge:   { backgroundColor: '#eff6ff', color: '#3b82f6', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' },
  emptyRow:     { padding: '60px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' },
  loadMoreWrapper: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', padding: '24px 0' },
  loadMoreBtn:  { padding: '10px 24px', backgroundColor: '#ffffff', border: '1.5px solid #e2e8f0', color: '#475569', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  loadMoreBtnDisabled: { padding: '10px 24px', backgroundColor: '#f8fafc', border: '1.5px solid #e2e8f0', color: '#94a3b8', borderRadius: '8px', fontSize: '13px', cursor: 'not-allowed' },
  loadMoreHint: { fontSize: '12px', color: '#94a3b8' },
  allLoadedHint:{ textAlign: 'center', padding: '16px 0', fontSize: '12px', color: '#cbd5e1' },
};
